/* eslint-disable array-callback-return */
import { scrollToEl, validOption, weightCalc } from './helpers';
import { SpecSheet } from './SpecSheet';

export class EmProductConfigurator {
	// The URL for the site's Product Config REST API endpoint.
	url = '';

	// The container that holds the product configurator data.
	container = '';

	// The config IDs selected to display on the page.
	configs = '';

	// All the option data for each of the configs selected.
	configData = [];

	// Placeholders for the spec sheet data and images.
	specSheetData = [];

	// Wattge data, as imported from Salesforce.
	wattageData = [];

	// The headings for the config table.
	tableHeadings = [];

	// The formatted <div< containing the information for each option in the grid.
	configDivs = [];

	// The ruleset for the current configs, to determine which options are selectable.
	currentRuleset = '';

	// A list of all the IDs selected in the data-match attribute. Used in rebuilding a ruleset once a rule has been removed.
	rulesetUniqueIDs = [];

	// The user-selected characters in the configurator.
	selectedProductID = [];

	// Configurator options that only have one character in a category.
	singleOptionIDs = [];

	// The full product ID, both single options and selected options, in one big array.
	combinedProductID = {
		// User selected options.
		selectedOptions: [],

		// Spec sheet data, generated from seleced options.
		specData: [],

		// The product number.
		productNumber: '',

		productFamily: '',

		characters: [],

		// Certifications.
		certification: '',
		lumens: '',
		wattage: '',

		weightLB: '',
		shippingWeightLB: '',
		weightKG: '',
		shippingWeightKG: '',

		controls: '',
	};

	constructor(productConfig) {
		this.productConfig = productConfig;

		this.getGlobalElements();
		this.initProductConfigurator();
		this.handleSelectableOptionUpdate();
	}

	getGlobalElements = () => {
		// REST API URL to obtain the data.
		this.url = `${window.location.origin}/wp-json/wp/v2/product_config`;
		this.container = this.productConfig.querySelector(
			'.product-configurator__container',
		);

		// The IDs of the Product Configurator Posts.
		this.configs = this.container.getAttribute('data-configs');
		this.options = this.productConfig.querySelector(
			'.product-configurator__options',
		);
		this.specDetail = this.productConfig.querySelector(
			'.product-configurator__specdetail',
		);
		this.specSheetBtn = this.productConfig.querySelector(
			'.spec-sheet-download',
		);
		this.resetBtn = this.productConfig.querySelector('.reset-config');
		this.loadingAnim = this.productConfig.querySelector('.config-loading');
		this.configName = this.productConfig.dataset.name;
	};

	initProductConfigurator() {
		this.fetchConfigData(this.url, this.configs);

		this.specSheetBtn.addEventListener('click', (e) => {
			e.preventDefault();
			const productSpecSheet = new SpecSheet(this.combinedProductID);
			productSpecSheet.generateSpecSheet();
		});

		this.resetBtn.addEventListener('click', (e) => {
			e.preventDefault();
			this.resetConfigOptions();
		});
	}

	getLumensAndWattageCalc = () => {
		const allProdSpecData = this.combinedProductID.specData;

		let baseLumens = '';
		const lumen_factors = [];

		for (const [id, specData] of Object.entries(allProdSpecData)) {
			for (const [key, data] of Object.entries(specData.data)) {
				switch (key) {
					case 'Base Lumens':
						baseLumens = parseFloat(data);
						break;
					case 'Lumen Factor':
						lumen_factors.push(parseFloat(data));
						break;
					default:
						break;
				}
			}
		}

		// Calculate the lumens from the spec sheet data options.
		const lumenSum = parseFloat(
			lumen_factors.length > 0
				? lumen_factors.reduce((value, a) => value + a, 0)
				: 0,
		);
		this.combinedProductID.lumens =
			Math.round((baseLumens * ((100 - lumenSum) / 100)) / 100) * 100;

		// Generate a part number for use in looking up the wattage in the provided wattage table.
		const wattageID = `${this.combinedProductID.characters[2]}${this.combinedProductID.characters[3]}${this.combinedProductID.characters[5]}${this.combinedProductID.characters[7]}${this.combinedProductID.characters[8]}${this.combinedProductID.characters[14]}`;
		this.combinedProductID.wattage = this.wattageData[wattageID]
			? this.wattageData[wattageID]
			: 0;

		// Specific fix for the GRP linear series and Amber/Green CCT & CRI to return a blank spot for the lumens on the spec table.
		if (
			this.combinedProductID.characters[2] === 'P2' ||
			this.combinedProductID.characters[2] === 'P4'
		) {
			if (
				this.combinedProductID.characters[6] === 'A' ||
				this.combinedProductID.characters[6] === 'G'
			) {
				this.combinedProductID.lumens = '';
			}
		}
	};

	/**
	 * Loops through the spec data to look for data that indicated product weight.
	 *
	 * Uses a helper function to calculate the weight for both LB and KG.
	 */
	getProductWeightCalc = () => {
		const allProdSpecData = this.combinedProductID.specData;

		const weight = [];
		const shippingWeight = [];

		Object.values(allProdSpecData).forEach((specData) => {
			Object.keys(specData.data).forEach((key) => {
				switch (key) {
					case 'Weight Adder':
						weight.push(parseFloat(specData.data[key]));
						break;
					case 'Shipping Weight_Adder':
						shippingWeight.push(parseFloat(specData.data[key]));
						break;
					default:
						break;
				}
			});
		});

		// Calculate total weights
		this.combinedProductID.weightLB = weightCalc(weight, 'lb');
		this.combinedProductID.weightKG = weightCalc(weight, 'kg');

		// Calculate total shipping weights
		this.combinedProductID.shippingWeightLB =
			weightCalc(shippingWeight, 'lb') + this.combinedProductID.weightLB;
		this.combinedProductID.shippingWeightKG =
			weightCalc(shippingWeight, 'kg') + this.combinedProductID.weightKG;
	};

	setControlsInfo = (productID) => {
		const specData = productID.specData;
		for (let i = 0; i < specData.length; i++) {
			if (specData[i].nchart_option === 'Controls') {
				let controls = specData[i].full_desc;
				if (controls) {
					controls = controls.split(';');
					const formattedControls = {
						heading: controls[0],
						content: [],
					};

					controls.shift();

					controls.forEach((item) => {
						item = item.split(': ');
						if (item.length > 1) {
							formattedControls.content.push([item[0], item[1]]);
						} else {
							formattedControls.content.push(item);
						}
					});
					return formattedControls;
				} else {
					return {};
				}
			}
		}
	};

	setProductSpecData = (productID) => {
		const selectedOptions = productID.selectedOptions;
		let certificationID = selectedOptions.find(
			(selected) => parseInt(selected.position) === 3,
		).match;
		if (Array.isArray(certificationID)) {
			certificationID = certificationID[0];
		}
		let productNumber = '';
		let productCharacters = [];
		let specData = [];

		selectedOptions.map((character) => {
			productNumber += `${character.partnum}`;
			productCharacters[character.position] = character.partnum;
		});

		selectedOptions.forEach((selectedChar) => {
			let unique = '';
			let desc = '';

			if (Array.isArray(selectedChar.match)) {
				selectedChar.match = selectedChar.match.join(';');
			}

			const idMatch = selectedChar.match.split(';');

			if (idMatch.length > 1) {
				for (let i = 0; i < idMatch.length; i++) {
					const matchedSeries = idMatch[i].slice(0, 2);
					if (matchedSeries === productCharacters[2]) {
						unique = idMatch[i];
						break;
					}
				}
			} else {
				unique = selectedChar.match;
			}

			const specsheet = this.specSheetData.find(
				(data) => data.spec_sheet_id === unique,
			);

			const optionData =
				this.configData.find((data) => data.sf_id === unique) || '';

			if (optionData.nchart_option === 'Product Series') {
				productID.productSeries = optionData.cust_description;
			}

			const formattedJSON = this.formatSpecSheetJSON(specsheet.spec_sheet_data);

			if (formattedJSON.Unique === certificationID) {
				let certification = formattedJSON.Description;
				if (certification) {
					certification = certification.split(';');
					productID.certification = certification;
				}
				productID.productFamily = formattedJSON['Product Family'];
			}

			if (optionData.cust_description) {
				desc = optionData.cust_description;
			} else if (formattedJSON.Description) {
				desc = formattedJSON.Description;
			} else {
				desc = 'Standard option';
			}

			specData.push({
				id: unique,
				nchart_option: optionData.nchart_option,
				desc,
				full_desc: formattedJSON.Description,
				data: formattedJSON,
			});
		});

		return { productNumber, productCharacters, specData };
	};

	getSelectedProductInformation = () => {
		this.combinedProductID = {
			selectedOptions: [],
			specData: [],
			productNumber: '',
			productFamily: '',
			characters: [],
			certification: '',
			lumens: '',
			wattage: '',
			weightLB: '',
			shippingWeightLB: '',
			weightKG: '',
			shippingWeightKG: '',
			controls: '',
		};

		// Combine the selected options with the pre-selected single options.
		this.combinedProductID.selectedOptions = [
			...this.selectedProductID,
			...this.singleOptionIDs,
		];

		// Sort the options by their position in the product number,
		this.combinedProductID.selectedOptions.sort(
			(a, b) => a.position - b.position,
		);

		// Set the product number, product characters, and spect data.
		const productSpecData = this.setProductSpecData(this.combinedProductID);
		this.combinedProductID.productNumber = productSpecData.productNumber;
		this.combinedProductID.characters = productSpecData.productCharacters;
		this.combinedProductID.specData = productSpecData.specData;

		// Set the product controls info.
		this.combinedProductID.controls = this.setControlsInfo(
			this.combinedProductID,
		);

		// Set the product config name.
		this.combinedProductID.configName = this.configName;

		const symbolMatch = this.configName.match(/['"\u0040\u0026\u2122\u00ae]/g);

		const nameCheck = this.configName.split(' ');
		//console.log(symbolMatch);
		if (symbolMatch !== null) {
			if (symbolMatch[0] === '™') {
				symbolMatch[0] = '\u2122';
			}
		}
		//console.log(nameCheck);
		if (this.combinedProductID.productFamily !== nameCheck[0]) {
			nameCheck[0] = `${this.combinedProductID.productFamily}${symbolMatch ?? symbolMatch[0]}`;
			this.combinedProductID.configName = nameCheck.join(' ');
		} else {
			this.combinedProductID.configName = this.configName;
		}

		if (this.combinedProductID.configName === 'ProSite Floodlight') {
			const isHighMast = this.combinedProductID.characters[10] === 'T';
			const isSlipFitter =
				this.combinedProductID.characters[10] === 'S' &&
				this.combinedProductID.characters[3] === 'D';

			this.combinedProductID.configName = isHighMast
				? 'ProSite LED High Mast'
				: 'ProSite LED Floodlight';
			this.combinedProductID.productSeries = isHighMast
				? 'ProSite LED High Mast'
				: 'ProSite LED Floodlight';

			if (isSlipFitter) {
				// Change the product certification output if it's the ProSite series and the user selectes the Slipfitter mounting.
				this.combinedProductID.certification = '';
				this.combinedProductID.certification = [
					'10 year warranty',
					'Class I Division 2 (Groups A, B, C, D)',
				];
			}
		}

		if (this.combinedProductID.configName === 'ProSite Streetlight') {
			const isHighMast = this.combinedProductID.characters[10] === 'T';

			this.combinedProductID.configName = isHighMast
				? 'ProSite LED High Mast'
				: 'ProSite LED Streetlight';
			this.combinedProductID.productSeries = isHighMast
				? 'ProSite LED High Mast'
				: 'ProSite LED Streetlight';
		}

		switch (this.combinedProductID.productSeries) {
			case 'Area Light':
				const certAndVoltageMatch =
					this.combinedProductID.characters[3] === 'U' &&
					this.combinedProductID.characters[7] === '5';
				if (certAndVoltageMatch) {
					this.combinedProductID.productFamily = 'DuroSite';
					this.combinedProductID.configName = 'Durosite® LED Area Light';
					this.combinedProductID.certification[0] = '5 year warranty';
				}
				break;
			case 'High Bay':
				['D', 'A', 'C', 'M', 'P'].includes(this.combinedProductID.characters[3])
					? (this.combinedProductID.productFamily = 'SafeSite')
					: (this.combinedProductID.productFamily = 'Vigilant');
				break;
			case 'GRP LED Linear, 4ft':
			case 'GRP LED Linear, 2ft':
				if (
					this.combinedProductID.characters[3] === 'C' ||
					this.combinedProductID.characters[3] === 'U'
				) {
					this.combinedProductID.productFamily = 'Vigilant';
					this.combinedProductID.configName =
						this.combinedProductID.configName.replace(/SafeSite/g, 'Vigilant');
				}
				break;
			default:
				break;
		}

		this.getLumensAndWattageCalc();
		this.getProductWeightCalc();
	};

	/**
	 * Run through the spec sheet data and format the keys for ease of use.
	 */

	formatSpecSheetJSON = (specSheetData) => {
		const specJSON = JSON.parse(specSheetData);

		const formattedJSON = {};

		// Replace the underscores with spaces, and extra text with blank characters.
		Object.keys(specJSON).forEach((key) => {
			if (key in specJSON) {
				const formattedKey = key
					.replace('__c', '')
					.replace('_', ' ')
					.replace('Spec Sheet_', '');
				formattedJSON[formattedKey] = specJSON[key];
			}
		});

		// Formats the special characters, to return them as characters and not entities.
		Object.entries(formattedJSON).forEach(([key, value]) => {
			formattedJSON[key] = value
				.replace(/&lt;/g, '<')
				.replace(/&gt;/g, '>')
				.replace(/&quot;/g, '"')
				.replace(/&amp;/g, '&');
		});

		return formattedJSON;
	};

	/**
	 * Handles the removal of a rule from a ruleset.
	 */
	handleRuleRemoval = (ruleset, removedRule) => {
		const index = ruleset.findIndex(
			(rule) => rule.position === removedRule.position,
		);
		if (index !== -1) {
			ruleset.splice(index, 1);
		}
	};

	/**
	 * Reset the configurator back to basics.
	 */
	resetConfigOptions = () => {
		// Hide the spec sheet generation function.
		this.specDetail.classList.remove('loaded');

		const options = [...this.productConfig.querySelectorAll('.option-data')];

		// Set all options to be selectable once again.
		options.map((option) => {
			option.classList.remove('active');
			option.classList.add('selectable');
			option.setAttribute('data-match', '');
			option.parentElement.querySelector('.selected-character').innerText = '';
		});

		// Reset all the variables to their default values.
		this.currentRuleset = '';
		this.rulesetUniqueIDs = [];
		this.selectedProductID = [];

		this.combinedProductID = {
			selectedOptions: [],
			specData: [],
			productNumber: '',
			certification: '',
			lumens: '',
			wattage: '',
			weightLB: '',
			shippingWeightLB: '',
			weightKG: '',
			shippingWeightKG: '',
			controls: '',
			characters: [],
		};

		// Pre-select the single options.
		this.getSingleOptions();
	};

	/**
	 * Get the headings for the option table, based on the NChart Option for each character.
	 */
	getTableHeadings = (configData) => {
		this.tableHeadings = [];

		configData.forEach((data) => {
			this.tableHeadings = this.tableHeadings.concat({
				label: data.nchart_option,
				pos: data.character_pos,
			});
		});

		const set = new Set();

		this.tableHeadings = this.tableHeadings.filter(
			(option) => !set.has(option.label) && set.add(option.label),
		);
	};

	/**
	 * Create the HTML container for each of the table headings.
	 */
	updateHeadings = (tableHeadings) => {
		let allHeadings = [];
		tableHeadings.forEach((heading) => {
			const headingContainer = document.createElement('div');
			headingContainer.className = 'option-heading';
			headingContainer.dataset.position = heading.pos;
			headingContainer.innerHTML = `<div class="selected-character"></div><div class="option-heading__text">${heading.label}</div>`;
			allHeadings = allHeadings.concat(headingContainer);
		});

		allHeadings.sort((a, b) => a.dataset.position - b.dataset.position);

		allHeadings.forEach((heading) => this.options.appendChild(heading));
	};

	/**
	 * Loop through the configuration data and set the attributes for each option.
	 */
	setConfigDivsIDs = () => {
		for (let i = 0; i < this.configData.length; i++) {
			// Get the partnum and the postion for the option.
			const dataPartnum = this.configData[i].partnum_character;
			const dataPosition = this.configData[i].character_pos;

			// Get the Salesforce ID.
			const sfID = this.configData[i].sf_id;

			if (sfID !== '') {
				for (let x = 0; x < this.configDivs.length; x++) {
					// For every single option created by the configurator, get the partnum and the position.
					const divPartnum = this.configDivs[x].getAttribute('data-partnum');
					const divPosition = this.configDivs[x].getAttribute('data-position');

					if (dataPartnum === divPartnum && dataPosition === divPosition) {
						// Get the sfIDs already applied to the option.
						let sfIDs = this.configDivs[x].getAttribute('data-sf');
						// Add the new SFID to the list, if the position and the partnum match but it isn't set in the attribute.
						sfIDs = sfIDs ? `${sfIDs};${sfID}` : sfID;
						// Update the SFIDs to include the SFIDs for the div.
						this.configDivs[x].setAttribute('data-sf', sfIDs);
					}
				}
			}
		}
	};

	getUniqueConfigOptions = (configDiv) => {
		const HighBayConfigs = '138930, 138931, 138932, 138945, 138946, 138947';
		const LowBayConfigs = '138941, 138942, 138943, 138948, 138949, 138950';

		const uniqueDataSet = new Set();

		configDiv.forEach((option) => {
			const position = option.dataset.position;
			const partnum = option.dataset.partnum;
			const key = `${position}-${partnum}`;

			if (!uniqueDataSet.has(key)) {
				uniqueDataSet.add(key);
			} else {
				option.setAttribute('data-sf', '');
			}
		});

		this.configDivs = [...uniqueDataSet].map((key) => {
			const [position, partnum] = key.split('-');
			return configDiv.find(
				(option) =>
					option.dataset.position === position &&
					option.dataset.partnum === partnum,
			);
		});

		this.configDivs.sort((a, b) => {
			if (a.dataset.position === '2' && b.dataset.position === '2') {
				if (this.configs === HighBayConfigs) {
					const order = { HE: 1, HC: 2, HW: 3, VE: 4, VC: 5, VW: 6 };
					return order[a.dataset.partnum] - order[b.dataset.partnum];
				} else if (this.configs === LowBayConfigs) {
					const order = { LE: 1, LC: 2, LW: 3, VE: 4, VC: 5, VW: 6 };
					return order[a.dataset.partnum] - order[b.dataset.partnum];
				}
			}

			return a.dataset.partnum.localeCompare(b.dataset.partnum);
		});

		this.setConfigDivsIDs();
	};

	/**
	 * Creates the spec data on hover HTML for options with a customer description.
	 */
	createOptionSpecHover = (description) => {
		let formattedDesc = '';
		if (description) {
			const descArray = description.split(';');
			if (descArray.length > 1) {
				descArray.forEach((desc) => {
					const description = desc.split('|');
					if (description.length > 1) {
						const line1 = description[0].split(':');
						const subhead = line1[0];
						description.shift();
						line1.shift();
						const details = line1.concat(description);
						formattedDesc += `<div class="single-desc"><div class="subhead">${subhead}:</div><div class="content">`;
						formattedDesc += details
							.map((detail) => `${detail.trim()}<br />`)
							.join('');
						formattedDesc += '</div></div>';
					} else {
						formattedDesc += `<div class="single-line">${desc}</div>`;
					}
				});
			}
		}

		return description
			? `<div class="single-spec"><span class="spec-data__detail">${formattedDesc || description}</span></div>`
			: '';
	};

	/**
	 * Creates the HTML container for each of the options within the configurator table.
	 */
	createOptionContainer = (option, specData) => {
		const optionContainer = document.createElement('div');
		const dataset = optionContainer.dataset;
		dataset.partnum = option.partnum_character;
		dataset.position = option.character_pos;

		const retroFitOptions = ['A', 'C', 'E', 'G', 'H', 'K', 'M', 'P'];
		const classList = optionContainer.classList;

		if (
			this.configs.includes('138944') &&
			parseInt(dataset.position) === 10 &&
			retroFitOptions.includes(option.partnum_character)
		) {
			classList.add('option-data', 'selectable', 'retroFitOption');
		} else {
			classList.add('option-data', 'selectable');
		}

		const optionDescription = option.cust_description
			? option.cust_description
			: 'Standard option';
		const optionHtml = `<div class="data-container"><span class="option-partnum">${option.partnum_character}</span><span class="option-desc">${optionDescription}</span></div>`;
		const specDataHTML = specData
			? `<div class="spec-data-contain">${specData}</div>`
			: '';

		optionContainer.innerHTML = optionHtml + specDataHTML;
		return optionContainer;
	};

	/**
	 * Takes all of the configuration data from Salesforce and then formats the information for use in the table.
	 */
	formatOptionData = (configData) => {
		const configDiv = [];

		configData.forEach((option) => {
			// Get the options' spec sheet data and then format it for use.
			const specData = this.specSheetData.find(
				(data) => data.spec_sheet_id === option.sf_id,
			)?.spec_sheet_data;
			const specDataFormat = this.formatSpecSheetJSON(specData);

			// Create the HTML container for the option and it's on-hover data.
			const specDataHTML = this.createOptionSpecHover(
				specDataFormat.Description,
			);
			const optionContainer = this.createOptionContainer(option, specDataHTML);

			configDiv.push(optionContainer);
		});

		this.getUniqueConfigOptions(configDiv);
	};

	/**
	 * Onclick, take the user's selected option and add it's partnum to the designated spot above the category label.
	 */
	updateProductNumber = (option) => {
		if (!validOption(option)) {
			return;
		}

		const selectedCharacterElement = option.parentElement.querySelector(
			'.selected-character',
		);
		const activeOption = option.parentElement.querySelector(
			'.option-data.active',
		);

		if (activeOption) {
			activeOption.classList.remove('active');
		}

		option.classList.add('active');
		selectedCharacterElement.innerText = option.getAttribute('data-partnum');
	};

	/**
	 * Option selection functionality.
	 */
	handleSelectableOptionUpdate = () => {
		this.productConfig.addEventListener('click', (e) => {
			const optionDataContainer = e.target.closest('.option-data.selectable');
			if (
				optionDataContainer &&
				!optionDataContainer.classList.contains('single-option')
			) {
				const selected = optionDataContainer;
				selected.classList.contains('active')
					? this.handleOptionRemoval(selected)
					: this.handleOptionSelection(selected);
			}
		});
	};

	/**
	 * Compare the ruleset and enable the selectable options based on the update ruleset.
	 */
	enableSelectableOptions = () => {
		this.configOptions = this.productConfig.querySelectorAll(
			'.option-data:not(.active)',
		);

		const configRuleset = new Set(
			this.currentRuleset.flatMap((rule) => rule.partnum_characters),
		);

		for (let i = 0; i < this.configOptions.length; i++) {
			const option = this.configOptions[i];
			const ids = option.getAttribute('data-sf').split(';');
			option.setAttribute('data-match', '');

			let isSelectable = false;
			let currentMatches = '';

			for (let j = 0; j < ids.length; j++) {
				const id = ids[j];

				if (configRuleset.has(id)) {
					isSelectable = true;
					currentMatches += (currentMatches ? ';' : '') + id;
				}
			}

			option.setAttribute('data-match', currentMatches);

			if (isSelectable) {
				option.classList.add('selectable');
			} else {
				option.classList.remove('selectable');
			}
		}
	};

	/**
	 * Checks if there's a ruleset in place.
	 */
	checkRuleset = (sfIDs) => {
		if (!this.currentRuleset) {
			this.setRuleset(sfIDs);
		} else {
			this.updateRuleset(sfIDs);
		}
	};

	/**
	 * Get the SFIDs for the newly selected option, and handles the ruleset comparison.
	 */
	handleRulesetCheck = (option) => {
		const sfIDs = option.dataset.sf.split(';');

		if (!this.currentRuleset) {
			option.setAttribute('data-match', option.dataset.sf);
		}

		this.checkRuleset(sfIDs);
		this.enableSelectableOptions();
	};

	handleOptionSelection = (option) => {
		// When an option is selected, start by updating the product number with the selection.
		this.updateProductNumber(option);

		// Then check the ruleset for the selected option.
		this.handleRulesetCheck(option);

		// Ensure that only one copy of the uniquely selected IDs is stored.
		this.rulesetUniqueIDs.push({
			position: option.dataset.position,
			match: option.dataset.match,
			sf: option.dataset.sf,
		});

		// Add it to the selected product ID. Used to generate spec sheet data.
		this.selectedProductID.push({
			position: option.dataset.position,
			partnum: option.dataset.partnum,
			match: option.dataset.match,
		});

		const selectedOptions = this.selectedProductID;
		const singleOptions = this.singleOptionIDs;

		const combined = selectedOptions.concat(singleOptions);

		if (combined.length === 13) {
			this.getSelectedProductInformation();
			this.specDetail.classList.add('loaded');
			const title = this.specDetail.querySelector('.specdetail__title');
			title.innerHTML = `<span class="partnum-prefix">Part Number: </span><span class="product-series">${this.combinedProductID.productNumber.slice(0, 2)}</span><span class="product-number">${this.combinedProductID.productNumber.slice(2, 14)}</span>`;
			if (window.innerWidth > 1000) {
				scrollToEl(this.specDetail, 50);
			}
		} else {
			this.specDetail.classList.remove('loaded');
		}
	};

	handleOptionRemoval = (option) => {
		// Clear the previously selected character in the table.
		const partnumText = option.parentElement.querySelector(
			'.selected-character',
		);
		option.classList.remove('active');
		partnumText.innerText = '';

		// Get the position, partnum, and matches from the deselected options.
		const { position, partnum, match } = option.dataset;

		// Remove hte option from the selected poduct ID and the ruleset.
		this.handleRuleRemoval(this.selectedProductID, { position, partnum });
		this.handleRuleRemoval(this.rulesetUniqueIDs, { position, match });

		// If there are only single options left, reset the config.
		if (this.selectedProductID.length === 0) {
			this.resetConfigOptions();
		} else {
			// Reset the current ruleset.
			this.currentRuleset = '';

			// Hide the spec detail / generation option since not every character is selected.
			this.specDetail.classList.contains('loaded') ??
				this.specDetail.classList.remove('loaded');

			// Take the unique IDs from the ruleset with the deselected option removed, and update the ruleset.
			this.rulesetUniqueIDs.forEach((id) => {
				const matches = id.sf.split(';');
				this.checkRuleset(matches);
			});

			// Enabled the selectable options.
			this.enableSelectableOptions();
		}
	};

	setRuleset = (optionIDs) => {
		const initialRuleset = [];

		optionIDs.forEach((id) => {
			const optionData = this.configData.find((data) => id === data.sf_id);
			optionData.character_rules.map((rule) => {
				initialRuleset.push(rule);
			});
		});

		const combinedRuleset = Object.values(
			initialRuleset.reduce((option, rule) => {
				const characterPos = rule.character_pos;
				const partnumCharacters = rule.partnum_characters;

				if (!option[characterPos]) {
					option[characterPos] = {
						character_pos: characterPos,
						partnum_characters: [],
					};
				}

				option[characterPos].partnum_characters =
					option[characterPos].partnum_characters.concat(partnumCharacters);

				return option;
			}, {}),
		);

		this.currentRuleset = combinedRuleset;
	};

	/**
	 * Based on the user selection, update the ruleset for the configurator.
	 */
	updateRuleset = (optionIDs) => {
		const incomingRuleset = [];

		optionIDs.forEach((id) => {
			const optionData = this.configData.find((data) => id === data.sf_id);
			optionData.character_rules.map((rule) => {
				incomingRuleset.push(rule);
			});
		});

		const selectedRules = new Set(
			incomingRuleset.flatMap((rule) => rule.partnum_characters),
		);

		const updatedRules = this.currentRuleset.map((rule) => ({
			character_pos: rule.character_pos,
			partnum_characters: rule.partnum_characters.filter((partnum) =>
				selectedRules.has(partnum),
			),
		}));

		this.currentRuleset = '';

		this.currentRuleset = updatedRules;
	};

	/**
	 * Pre-select the single options in each heading that have no other options.
	 */
	getSingleOptions = () => {
		this.singleOptionIDs = [];
		const singleOptionSFIDS = [];

		/**
		 * For loop starts at 2 because that's the first character position in the configurator.
		 */
		for (let i = 2; i < 15; i++) {
			const positionedElements = this.productConfig.querySelectorAll(
				`.option-data[data-position="${i}"]`,
			);
			if (positionedElements.length === 1) {
				positionedElements[0].classList.add('single-option');
				const sfIDs = positionedElements[0].dataset.sf.split(';');

				sfIDs.forEach((id) => singleOptionSFIDS.push(id));

				positionedElements[0].classList.add('active');
				positionedElements[0].parentElement.querySelector(
					'.selected-character',
				).innerText = positionedElements[0].getAttribute('data-partnum');
				positionedElements[0].setAttribute(
					'match',
					positionedElements[0].dataset.sf,
				);

				/**
				 * Single options live in their own array, for ease of resetting and updating on option selection.
				 */
				this.singleOptionIDs.push({
					position: positionedElements[0].dataset.position,
					partnum: positionedElements[0].dataset.partnum,
					match: sfIDs,
				});
			}
		}
	};

	/**
	 * Add the generated options to the product configurator table.
	 */
	populateOptionTable = () => {
		this.updateHeadings(this.tableHeadings);

		this.formatOptionData(this.configData);

		this.optionHeadings = document.querySelectorAll('.option-heading');

		this.optionHeadings.forEach((heading) => {
			const characterPosition = heading.getAttribute('data-position');

			// Dialight asked for a specific configuration of Mounting Options in the table for the Area Light config.
			if (
				this.configs.includes('138944') &&
				parseInt(characterPosition) === 10
			) {
				const mountingOptionOrder = [
					'B',
					'N',
					'A',
					'C',
					'E',
					'G',
					'H',
					'K',
					'M',
					'P',
					'D',
					'F',
					'R',
					'S',
					'T',
					'U',
					'V',
					'W',
					'Y',
				];

				mountingOptionOrder.forEach((optionCharacter) => {
					const html = this.configDivs.find((option) => {
						return (
							option.getAttribute('data-partnum') === optionCharacter &&
							option.getAttribute('data-position') === characterPosition
						);
					});

					if (html) {
						heading.appendChild(html);
					}
				});
			} else {
				// Append the created configDivs to the proper table heading based on the data-position attribute.
				this.configDivs.forEach((option) => {
					const optionPosition = option.getAttribute('data-position');
					if (optionPosition === characterPosition) {
						heading.appendChild(option);
					}
				});
			}
		});
	};

	/**
	 * Get the ruleset for each option in the configurator.
	 */
	formatCharacterRules = (configData) => {
		configData.map((data) => {
			// Get the JSON data from Salesforce to parse the character rules for each piece of data.
			const rulesetJSON = JSON.parse(data.character_rules);

			// Create every option's ruleset based on their SF data.
			const ruleset = rulesetJSON.map((value) => {
				const singleRule = {};
				singleRule.character_pos = (rulesetJSON.indexOf(value) + 2).toString();
				singleRule.partnum_characters = value.split(';');

				return singleRule;
			});

			data.character_rules = '';
			data.character_rules = ruleset;
		});
	};

	fetchConfigData = async (restURL, configs) => {
		// Query the REST API for the config data, filtering only by the selected product configs.
		restURL = `${restURL}?include=${configs}`;

		const response = await fetch(restURL);

		let data;

		try {
			data = await response.json();
		} catch (error) {
			console.log(error);
		} finally {
			// If there's data, grab the nchart options and populate the configData array.
			if (data) {
				this.productConfig
					.querySelector('.config-loading-anim')
					.classList.add('hidden');
				let wattages = [];

				data.forEach((data) => {
					this.configData = this.configData.concat(data.acf.nchart_options);
					this.specSheetData = this.specSheetData.concat(
						data.acf.spec_sheet_info,
					);

					wattages = wattages.concat(JSON.parse(data.acf.wattage_data));
				});

				this.wattageData = Object.assign({}, ...wattages);

				this.formatCharacterRules(this.configData);
				this.getTableHeadings(this.configData);
				this.populateOptionTable();
				this.getSingleOptions();
			}
		}
	};
}
